import type { ArticleContent } from './types';

const da: Record<string, ArticleContent> = {
  'allchinabuy-spreadsheet': {
    title: 'AllChinaBuy-regneark: komplet guide til produkter og sælgere',
    shortDescription: 'Den komplette guide til AllChinaBuy-regnearket — sådan finder du produkter, sælgere og shoppingressourcer ét sted.',
    metaTitle: 'AllChinaBuy-regneark: komplet guide til produkter og sælgere',
    metaDescription: 'Lær hvad AllChinaBuy-regnearket er, hvordan du bruger det til at finde produkter og sælgere, og organiser din research med denne guide for begyndere.',
    h1: 'AllChinaBuy-regneark: komplet guide til produkter og sælgere',
    intro: 'AllChinaBuy-regnearket er en praktisk måde at holde produkt-, sælger- og shoppingoplysninger organiseret i én let overskuelig liste. Uanset om du er ny til indkøbsagenter eller vil have en mere struktureret måde at researche på, hjælper et velorganiseret regneark dig med at sammenligne og træffe roligere beslutninger.',
    sections: [
      {
        heading: 'Hvad er AllChinaBuy-regnearket?',
        paragraphs: [
          'Et AllChinaBuy-regneark er en struktureret liste til at samle og sammenligne produkt- og sælgeroplysninger. Det er ikke et officielt værktøj, men en almindelig måde for købere at holde styr på links, størrelser, priser, lagernoter og QC-detaljer.',
          'Fordi køb via en agent ofte involverer mange links, forvandler et regneark spredte oplysninger til rækker og kolonner, du hurtigt kan scanne.',
        ],
        list: [
          'Produktnavne og direkte links',
          'Størrelser, farver og varianter',
          'Estimerede priser og antal',
          'Sælgernavne og vurderinger',
          'Lager- og QC-noter',
        ],
      },
      {
        heading: 'Hvorfor bruge et regneark til dine indkøb?',
        paragraphs: [
          'Den største fordel er klarhed. I stedet for links i browserhistorikken giver et regneark hver vare et fast sted, sorterbart efter kategori.',
          'En anden fordel er sammenligning: når to sælgere ligner hinanden, gør side-om-side-detaljer det nemmere at vurdere pris, fragt og feedback.',
        ],
      },
      {
        heading: 'Sådan bygger du dit eget regneark',
        paragraphs: [
          'Du behøver ingen særlig software. Start med de kolonner, der betyder mest, og tilføj flere efterhånden.',
          'En praktisk start inkluderer en kolonne for vare, link, pris, sælger og status.',
        ],
        list: [
          'Opret kolonner for vare, link, pris, sælger og status',
          'Tilføj én række pr. produkt',
          'Opdater status efterhånden',
          'Hold et separat ark til afsluttede ordrer',
        ],
      },
      {
        heading: 'Almindelige felter forklaret',
        paragraphs: [
          'Produktlinket er vigtigst, fordi det bevarer den præcise vare. Sælgernavnet hjælper dig med at huske, prisen med at følge totaler, og lager-/QC-felter med at følge varen efter modtagelse.',
          'Hold også en notes-kolonne til usædvanlige detaljer, som en størrelsesanbefaling eller en påmindelse om at tjekke mål.',
        ],
      },
    ],
    comparison: {
      title: 'Regnearkfelt og hvad det hjælper med',
      columns: ['Felt', 'Hvad det følger', 'Hvorfor det betyder noget'],
      rows: [
        ['Produktlink', 'Præcis vare-URL', 'Finder samme produkt igen'],
        ['Sælger', 'Hvem sælger varen', 'Sammenligner feedback'],
        ['Pris', 'Estimeret pris', 'Følger total før bestilling'],
        ['Status', 'Ordretrin', 'Viser hvad der mangler'],
        ['QC-note', 'Inspektionsresultat', 'Bekræfter varen matcher'],
      ],
    },
    faqs: [
      { q: 'Hvad er et AllChinaBuy-regneark?', a: 'En struktureret liste til at organisere produktlinks, sælgere, priser og fragt- eller QC-noter ét sted.' },
      { q: 'Er regnearket en officiel funktion?', a: 'Nej. Det er en konvention blandt købere, ikke et officielt AllChinaBuy-produkt.' },
      { q: 'Skal jeg bruge særlig software?', a: 'Nej. Ethvert grundlæggende regnearkværktøj virker, med enkle kolonner for vare, link, pris, sælger og status.' },
      { q: 'Hvad skal hver række indeholde?', a: 'Mindst link, sælger, pris og status, plus størrelse, farve, lager og QC efter behov.' },
    ],
  },

  'allchinabuy-review': {
    title: 'AllChinaBuy-anmeldelse: er det legitimt og sikkert?',
    shortDescription: 'Et ærligt, uafhængigt blik på AllChinaBuy — hvad det er, hvordan det virker, og hvad du bør overveje inden køb.',
    metaTitle: 'AllChinaBuy-anmeldelse: er det legitimt og sikkert?',
    metaDescription: 'En uafhængig AllChinaBuy-anmeldelse om hvordan indkøbsagenten virker, hvad du kan forvente, og praktiske råd til sikrere indkøb.',
    h1: 'AllChinaBuy-anmeldelse: er det legitimt og sikkert?',
    intro: 'AllChinaBuy er en indkøbsagent, der hjælper internationale købere med at bestille produkter fra kinesiske markedspladser og sende dem til udlandet. Denne uafhængige anmeldelse forklarer den generelle funktion, det typiske flow og hvad der er værd at tjekke inden din første ordre.',
    sections: [
      {
        heading: 'Sådan virker en indkøbsagent',
        paragraphs: [
          'En indkøbsagent fungerer som mellemled mellem dig og sælgerne. Du deler et produktlink, og agenten køber, opbevarer og sender varen til dit land.',
          'De fleste agenter, inklusive AllChinaBuy, følger samme flow: tilføjelse, køb, modtagelse på lager og valg af forsendelsesmetode.',
        ],
      },
      {
        heading: 'Hvad folk typisk bruger det til',
        paragraphs: [
          'Købere bruger ofte agenter til mode, sko, streetwear og accessories, der er svære at finde lokalt. Agenten hjælper også med at samle flere ordrer i én pakke.',
        ],
        list: [
          'Mode og streetwear',
          'Sko og sneakers',
          'Accessories og tasker',
          'Samle flere ordrer i én forsendelse',
        ],
      },
      {
        heading: 'Ting at tjekke inden bestilling',
        paragraphs: [
          'Da agenten er en service og ikke en markedspladsgaranti, er det værd at gennemgå gebyrer, forsendelsesmuligheder og opbevaringsregler inden du forpligter dig.',
          'At tjekke nyere feedback fra andre købere hjælper også med realistiske forventninger til levering og support.',
        ],
      },
      {
        heading: 'Et afbalanceret syn',
        paragraphs: [
          'En agent kan spare dig for at håndtere flere sælgere og sprog direkte, men tilføjer et trin og gebyrer. For mange er bekvemmeligheden det værd; for andre giver det mening at sammenligne først.',
        ],
      },
    ],
    comparison: {
      title: 'Via agent vs. direkte køb',
      columns: ['Faktor', 'Via agent', 'Direkte fra sælger'],
      rows: [
        ['International fragt', 'Agenten samler og sender', 'Ofte ikke tilgængelig'],
        ['Flere ordrer', 'Kan samles i én pakke', 'Hver sælger sender separat'],
        ['Sprogbarriere', 'Agenten håndterer kontakt', 'Du kontakter sælgeren'],
        ['Ekstra omkostning', 'Servicegebyrer', 'Normalt lavere'],
        ['QC-billeder', 'Ofte tilgængelige', 'Sjældent tilbudt'],
      ],
    },
    faqs: [
      { q: 'Er AllChinaBuy legitimt?', a: 'Det er en rigtig indkøbsagent-service. Som med enhver service er det klogt at gennemgå vilkår, gebyrer og nyere feedback.' },
      { q: 'Hvordan virker AllChinaBuy?', a: 'Du deler et link, agenten køber og opbevarer, og du vælger en forsendelsesmetode til udlandet.' },
      { q: 'Koster en agent mere?', a: 'Servicegebyrer gælder, men samling og international fragt er stadig praktisk. Sammenlign den samlede pris.' },
      { q: 'Kan jeg få QC-billeder?', a: 'Mange agenter tilbyder QC-billeder. Bekræft tilgængelighed og eventuelle gebyrer.' },
    ],
  },

  'allchinabuy-shipping-cost': {
    title: 'AllChinaBuy fragtomkostninger: komplet guide',
    shortDescription: 'Forstå hvad der påvirker AllChinaBuy fragtomkostninger, og estimér din total inden bestilling.',
    metaTitle: 'AllChinaBuy fragtomkostninger: komplet guide',
    metaDescription: 'En klar guide til AllChinaBuy fragtomkostninger — hvad der påvirker prisen, vægt og volumen, og råd til at holde regningen nede.',
    h1: 'AllChinaBuy fragtomkostninger: komplet guide',
    intro: 'Fragtomkostninger er ofte den største overraskelse for nye købere. Denne guide forklarer de vigtigste prisfaktorer, hvordan vægt og volumen beregnes, og enkle måder at holde totalen rimelig.',
    sections: [
      {
        heading: 'Hvad påvirker omkostningen?',
        paragraphs: [
          'Flere faktorer bestemmer prisen. De to største er pakkens vægt og destination. Forsendelsesmetode, mål og ekstra services spiller også ind.',
          'Da priser ændrer sig, bør du altid tjekke de aktuelle muligheder i din konto frem for gamle tilbud.',
        ],
        list: [
          'Pakkens vægt og størrelse',
          'Destinationsland',
          'Valgt forsendelsesmetode',
          'Forsikring og ekstra services',
          'Told- og afgiftsregler',
        ],
      },
      {
        heading: 'Vægt vs. volumenvægt',
        paragraphs: [
          'Kurérer fakturerer efter faktisk vægt eller volumenvægt — den højeste. Volumenvægt beregnes ud fra målene, så en stor men let vare kan koste mere.',
          'Derfor er kompakte, velpakkede pakker ofte billigere at sende.',
        ],
      },
      {
        heading: 'Sådan estimerer du din total',
        paragraphs: [
          'Start med den estimerede vægt, og sammenlign de tilgængelige linjer for din destination. De fleste agenter viser et live-estimat, når pakken er vejet.',
          'Glem ikke indenrigsfragt til lageret, servicegebyrer og eventuelle toldomkostninger.',
        ],
      },
      {
        heading: 'Praktiske måder at sænke prisen',
        paragraphs: [
          'At samle flere varer i én pakke er den mest effektive besparelse. At vælge en langsommere linje og fjerne voluminøs emballage hjælper også.',
        ],
        list: [
          'Saml flere varer i én pakke',
          'Fjern voluminøs emballage',
          'Sammenlign linjer og hastigheder',
          'Send tunge varer sammen når det giver mening',
        ],
      },
    ],
    comparison: {
      title: 'Fragtfaktor og hvad du skal tjekke',
      columns: ['Faktor', 'Hvad du tjekker', 'Hvorfor det betyder noget'],
      rows: [
        ['Vægt', 'Faktisk vs. volumen', 'Bestemmer faktureret vægt'],
        ['Destination', 'Dit land', 'Ændrer linjer og pris'],
        ['Metode', 'Tilgængelige muligheder', 'Hastighed og pris varierer'],
        ['Forsikring', 'Aktuelle vilkår', 'Beskytter dyre ordrer'],
        ['Told', 'Lokale regler', 'Undgår uventede gebyrer'],
      ],
    },
    faqs: [
      { q: 'Hvad koster AllChinaBuy fragt?', a: 'Det afhænger af vægt, størrelse, destination og linje. Tjek live-estimatet for din pakke.' },
      { q: 'Hvad er volumenvægt?', a: 'En faktureret vægt beregnet ud fra målene. Kurøren fakturerer faktisk eller volumen, den højeste.' },
      { q: 'Hvordan sænker jeg fragtomkostninger?', a: 'Saml varer, fjern voluminøs emballage, og sammenlign linjer og hastigheder.' },
      { q: 'Er told inkluderet?', a: 'Normalt ikke. Tjek dit lands regler for at undgå overraskelser.' },
    ],
  },

  'allchinabuy-shipping': {
    title: 'AllChinaBuy fragtguide: metoder, tider og råd',
    shortDescription: 'Hvordan AllChinaBuy fragt virker, de almindelige metoder og råd til en glat levering.',
    metaTitle: 'AllChinaBuy fragtguide: metoder, tider og råd',
    metaDescription: 'En praktisk guide til AllChinaBuy fragt — proces, almindelige metoder og råd til en glat levering.',
    h1: 'AllChinaBuy fragtguide: metoder, tider og råd',
    intro: 'At forstå fragt med en agent gør hele oplevelsen glattere. Denne guide gennemgår processen fra lager til dør, forklarer de almindelige metoder og deler råd til at undgå forsinkelser.',
    sections: [
      {
        heading: 'Processen trin for trin',
        paragraphs: [
          'Når dine varer ankommer til lageret, vejes og fotograferes de. Du vælger derefter en metode, betaler, og pakken afsendes.',
          'Du modtager normalt et trackingnummer. Leveringstiden afhænger af metode og destination.',
        ],
        list: [
          'Varer ankommer og vejes på lageret',
          'Du vælger en linje og bekræfter',
          'Pakken afsendes med trackingnummer',
          'Du følger den til levering',
        ],
      },
      {
        heading: 'Almindelige forsendelsesmetoder',
        paragraphs: [
          'Linjer falder i kategorier: ekspres, standard og økonomi. Ekspres er hurtigere men dyrere, økonomi billigere men langsommere.',
          'Nogle linjer passer bedre til bestemte destinationer eller størrelser, så sammenlign mulighederne for din pakke.',
        ],
      },
      {
        heading: 'Tracking og forsikring',
        paragraphs: [
          'De fleste metoder inkluderer tracking. Forsikring er ofte valgfri men værd at overveje for dyre ordrer.',
        ],
      },
      {
        heading: 'Råd til en glat levering',
        paragraphs: [
          'Tjek din adresse inden bekræftelse, og følg pakken for at reagere hurtigt på told- eller leveringsbeskeder. Samling reducerer også omkostning og antal leveringer.',
        ],
        list: [
          'Tjek din adresse omhyggeligt',
          'Overvej forsikring for værdifulde varer',
          'Følg din pakke regelmæssigt',
          'Saml ordrer for at spare',
        ],
      },
    ],
    comparison: {
      title: 'Metode og hvad du kan forvente',
      columns: ['Metode', 'Typisk hastighed', 'Pris'],
      rows: [
        ['Ekspres', 'Hurtigere', 'Højere'],
        ['Standard', 'Afbalanceret', 'Moderat'],
        ['Økonomi', 'Langsommere', 'Lavere'],
      ],
    },
    faqs: [
      { q: 'Hvor lang tid tager AllChinaBuy fragt?', a: 'Det afhænger af metode og destination. Ekspres er hurtigere, økonomi langsommere.' },
      { q: 'Kan jeg spore min pakke?', a: 'Ja, de fleste metoder inkluderer et trackingnummer.' },
      { q: 'Skal jeg købe forsikring?', a: 'Valgfri men anbefalet til dyre ordrer for at beskytte mod tab eller skade.' },
      { q: 'Kan jeg sende flere ordrer sammen?', a: 'Ja, samling reducerer antallet af forsendelser og den samlede pris.' },
    ],
  },

  'allchinabuy-coupon': {
    title: 'AllChinaBuy: guide til kuponer og rabatkoder',
    shortDescription: 'Sådan finder og bruger du AllChinaBuy-kuponer og rabatkoder, plus råd mod falske tilbud.',
    metaTitle: 'AllChinaBuy: guide til kuponer og rabatkoder',
    metaDescription: 'Lær hvordan AllChinaBuy-kuponer og rabatkoder virker, hvor du finder dem, og hvordan du undgår falske tilbud.',
    h1: 'AllChinaBuy: guide til kuponer og rabatkoder',
    intro: 'Kuponer og rabatkoder kan gøre indkøb via agent lidt billigere, hvis du forstår hvordan de virker. Denne guide forklarer hvor tilbud kommer fra, hvordan de anvendes, og hvordan du undgår falske eller udløbne koder.',
    sections: [
      {
        heading: 'Hvor rabatter kommer fra',
        paragraphs: [
          'Rabatter optræder ofte som kampagner, sæsontilbud eller koder delt på serviceegne kanaler.',
          'Da tilbud ændrer sig ofte, er det mest pålidelige sted platformens egne meddelelser frem for tilfældige tredjepartssider.',
        ],
      },
      {
        heading: 'Sådan anvender du en kode',
        paragraphs: [
          'Koder indtastes normalt ved betaling eller gælder en del af ordren. Læs vilkårene, da mange har minimumskøb, udløbsdato eller kategorirestriktion.',
          'Virker en kode ikke, så tjek stavning, dato og vilkår.',
        ],
      },
      {
        heading: 'Sådan undgår du falske kuponer',
        paragraphs: [
          'Vær på vagt over for sider, der lover store eller ubegrænsede rabatter. Legitime tilbud er beskedne og klart knyttet til vilkår.',
          'Del aldrig betalingsoplysninger på en tredjepartsside i bytte for en kode.',
        ],
      },
      {
        heading: 'Andre måder at spare på',
        paragraphs: [
          'Ud over koder sparer samling af ordrer og valg af den rigtige linje ofte mere end en lille rabat.',
        ],
        list: [
          'Tjek officielle meddelelser først',
          'Læs vilkår og udløbsdato',
          'Undgå sider med urealistiske rabatter',
          'Kombinér gyldige koder med samling',
        ],
      },
    ],
    comparison: {
      title: 'Legitimt tilbud vs. advarselstegn',
      columns: ['Tegn', 'Legitimt', 'Vær på vagt'],
      rows: [
        ['Kilde', 'Officielle kanaler', 'Ukendte tredjepartssider'],
        ['Værdi', 'Beskeden, realistisk', 'Store, ubegrænsede rabatter'],
        ['Vilkår', 'Klart angivet', 'Vage eller manglende'],
        ['Personlige oplysninger', 'Aldrig bedt om', 'Beder om følsomme data'],
      ],
    },
    faqs: [
      { q: 'Hvor finder jeg AllChinaBuy-kuponer?', a: 'Tjek platformens egne meddelelser og kampagner, da tilbud ændrer sig ofte.' },
      { q: 'Virker rabatkoder altid?', a: 'Ikke altid. De kan udløbe eller have minimumskøb og kategorirestriktioner.' },
      { q: 'Hvordan spotter jeg en falsk kupon?', a: 'Vær på vagt over for store rabatter og krav om personlige eller betalingsoplysninger.' },
      { q: 'Hvilke andre måder kan jeg spare på?', a: 'Samling og valg af den rigtige linje sparer ofte mere.' },
    ],
  },

  'allchinabuy-warehouse': {
    title: 'AllChinaBuy lagerguide: opbevaring, QC og fragt',
    shortDescription: 'Hvad AllChinaBuy-lageret gør, hvordan opbevaring og QC-billeder virker, og hvordan varer når dig.',
    metaTitle: 'AllChinaBuy lagerguide: opbevaring, QC og fragt',
    metaDescription: 'Forstå AllChinaBuy-lagerprocessen — hvordan varer opbevares, fotograferes til QC og klargøres til international fragt.',
    h1: 'AllChinaBuy lagerguide: opbevaring, QC og fragt',
    intro: 'Lageret er mellemstationen mellem sælgeren og din dør. Denne guide forklarer hvad der sker efter modtagelse, hvordan opbevaring og QC-billeder virker, og hvordan lageret klargør din pakke.',
    sections: [
      {
        heading: 'Hvad sker der når varer ankommer',
        paragraphs: [
          'Når en sælger sender din vare, ankommer den til lageret og registreres i din konto. Den vejes og måles, hvilket danner grundlag for fragtestimatet.',
          'Det er også her du typisk kan bede om QC-billeder for at bekræfte varen inden afsendelse.',
        ],
      },
      {
        heading: 'Sådan virker opbevaring',
        paragraphs: [
          'Lageret tilbyder ofte en gratis opbevaringsperiode, hvorefter der kan gælde gebyr. Det lader dig samle flere varer inden samlet forsendelse.',
          'Tjek den aktuelle politik for at vide hvor længe du kan opbevare gratis.',
        ],
      },
      {
        heading: 'QC-billeder og inspektioner',
        paragraphs: [
          'QC-billeder viser den faktiske vare inden afsendelse. Et grundsæt kan vise flere vinkler, mens detaljerede inspektioner tjekker mål, mærker eller specifikke detaljer.',
          'Ser noget forkert ud, har du bedre chance for at løse det, før pakken forlader lageret.',
        ],
      },
      {
        heading: 'Klargør din pakke til fragt',
        paragraphs: [
          'Når du er klar, samler lageret dine varer, pakker og afsender dem med den valgte metode. At fjerne ekstra emballage reducerer størrelse og pris.',
        ],
        list: [
          'Varer registreres, vejes og måles',
          'QC-billeder lader dig bekræfte inden fragt',
          'Gratis periode hjælper med samling',
          'Samling reducerer samlet pris',
        ],
      },
    ],
    comparison: {
      title: 'Lagertrin og hvad det betyder',
      columns: ['Trin', 'Hvad der sker', 'Din handling'],
      rows: [
        ['Ankomst', 'Vare registreret og vejet', 'Gennemgå i kontoen'],
        ['Opbevaring', 'Vare opbevaret', 'Saml med andre ordrer'],
        ['QC', 'Billeder af varen', 'Bed om og gennemgå'],
        ['Pakning', 'Varer samlet og pakket', 'Bekræft metode'],
      ],
    },
    faqs: [
      { q: 'Hvad er AllChinaBuy-lageret?', a: 'Faciliteten hvor dine varer opbevares, vejes og klargøres til fragt efter køb.' },
      { q: 'Er opbevaring gratis?', a: 'Ofte en gratis periode, derefter kan gebyr gælde. Tjek den aktuelle politik.' },
      { q: 'Hvordan virker QC-billeder?', a: 'Du beder om billeder af varen på lageret for at bekræfte den inden afsendelse.' },
      { q: 'Hvad er samling (konsolidering)?', a: 'At samle flere varer i én pakke for at reducere antal forsendelser og pris.' },
    ],
  },

  'allchinabuy-legit': {
    title: 'Er AllChinaBuy legitimt? Sikkerhed og anmeldelser',
    shortDescription: 'Et uafhængigt blik på om AllChinaBuy er legitimt og sikkert, med praktiske råd til trygge indkøb.',
    metaTitle: 'Er AllChinaBuy legitimt? Sikkerhed og anmeldelser',
    metaDescription: 'Spekulerer du på om AllChinaBuy er legitimt? Læs en uafhængig guide om legitimitet, bekymringer og praktiske sikkerhedsråd.',
    h1: 'Er AllChinaBuy legitimt? Sikkerhed og anmeldelser',
    intro: 'Hvis du er ny til indkøbsagenter, er det helt rimeligt at spørge om en service er legitim og sikker, før du sender penge. Denne guide giver et afbalanceret, uafhængigt syn.',
    sections: [
      {
        heading: 'Hvad legitim betyder for en agent',
        paragraphs: [
          'En indkøbsagent er en rigtig service, der køber for dig og sender internationalt. At være legitim betyder generelt at levere det lovede, kommunikere klart og håndtere problemer ansvarligt.',
          'Nøglen er at vurdere servicen med realistiske forventninger og samme forsigtighed som ved ethvert onlinekøb.',
        ],
      },
      {
        heading: 'Almindelige bekymringer og løsninger',
        paragraphs: [
          'De mest almindelige bekymringer er leveringstid, kommunikation og fragtpris. Mange håndteres ved at læse vilkårene, bruge QC-billeder og holde orden på oplysningerne.',
          'Er noget uklart, er det hurtigst at spørge agenten direkte inden bestilling.',
        ],
      },
      {
        heading: 'Praktiske sikkerhedsråd',
        paragraphs: [
          'Betal via officielle kanaler, gem dine kvitteringer, og bekræft fragtoplysninger. For dyre varer, overvej forsikring og QC-billeder.',
          'Del aldrig login eller betalingsoplysninger med nogen, der lover at fremskynde eller rabatere din ordre uden for processen.',
        ],
        list: [
          'Betal kun via officielle kanaler',
          'Brug QC-billeder til at bekræfte',
          'Gem ordrer og betalinger',
          'Overvej forsikring for dyre ordrer',
        ],
      },
      {
        heading: 'Træf en informeret beslutning',
        paragraphs: [
          'Der er intet enkelt svar for alle, men at være informeret fjerner det meste af risikoen. Læs nyere feedback, forstå gebyrer, og start med en lille ordre.',
        ],
      },
    ],
    comparison: {
      title: 'Advarselstegn og hvad du gør',
      columns: ['Situation', 'Normalt', 'Vær forsigtig'],
      rows: [
        ['Betaling', 'Officiel betaling', 'Anmodninger uden for platform'],
        ['Kontakt', 'Officiel support', 'Beder om login'],
        ['Tilbud', 'Realistiske vilkår', 'Urealistiske garantier'],
        ['Fragt', 'Sporet pakke', 'Ingen tracking'],
      ],
    },
    faqs: [
      { q: 'Er AllChinaBuy legitimt?', a: 'Det er en rigtig indkøbsagent-service. Gennemgå vilkår, gebyrer og nyere feedback inden bestilling.' },
      { q: 'Er AllChinaBuy sikkert?', a: 'Officiel betaling, QC-billeder og kvitteringer gør oplevelsen sikrere. Behandl det som ethvert onlinekøb.' },
      { q: 'Hvad skal jeg passe på?', a: 'Undgå betaling uden for officielle kanaler og del aldrig login eller betalingsoplysninger.' },
      { q: 'Skal jeg starte med en lille ordre?', a: 'Ja, det er en fornuftig måde at opbygge tillid på inden større ordrer.' },
    ],
  },

  'allchinabuy-qc': {
    title: 'AllChinaBuy QC-guide: læs QC-billeder',
    shortDescription: 'Hvordan AllChinaBuy QC-billeder virker, og hvad du skal tjekke inden afsendelse.',
    metaTitle: 'AllChinaBuy QC-guide: læs QC-billeder',
    metaDescription: 'Lær hvordan AllChinaBuy QC-billeder virker, hvad du skal tjekke, og hvordan du beder om nyttige inspektioner.',
    h1: 'AllChinaBuy QC-guide: læs QC-billeder',
    intro: 'QC-billeder er et af de mest værdifulde værktøjer ved køb via agent, fordi de viser den faktiske vare inden afsendelse. Denne guide forklarer hvordan de virker og hvad du skal tjekke.',
    sections: [
      {
        heading: 'Hvad QC-billeder er',
        paragraphs: [
          'QC-billeder (quality check) tages af din vare efter ankomst til lageret. De viser det rigtige produkt, ikke sælgerens billeder, så du kan opdage åbenlyse problemer inden afsendelse.',
        ],
      },
      {
        heading: 'Hvad du skal tjekke',
        paragraphs: [
          'Fokuser på de detaljer, der betyder noget. For tøj: størrelse, farve, syninger og tryk. For sko: form, logo og farve. For accessories: materialer og finish.',
          'Sammenlign med den oprindelige annonce. En farve- eller designforskel er værd at rejse inden afsendelse.',
        ],
        list: [
          'Farve- og designnøjagtighed',
          'Størrelse og mål',
          'Syninger, tryk og finish',
          'Skader eller defekter',
        ],
      },
      {
        heading: 'Sådan beder du om nyttige billeder',
        paragraphs: [
          'Vær specifik med de vinkler eller detaljer, du ønsker, som et nærbillede af et logo eller indersiden af en sko.',
          'Ser noget forkert ud, kontakt agenten mens varen stadig er på lageret.',
        ],
      },
      {
        heading: 'Brug QC-billeder til at beslutte',
        paragraphs: [
          'QC-billeder hjælper med at fange problemer tidligt, men de er billeder, ikke en fysisk inspektion. Hold realistiske forventninger til deres begrænsninger.',
        ],
      },
    ],
    comparison: {
      title: 'Hvad du tjekker efter varetype',
      columns: ['Vare', 'Nøgledetaljer', 'Almindelige problemer'],
      rows: [
        ['Tøj', 'Størrelse, farve, syninger', 'Forkert størrelse eller farve'],
        ['Sko', 'Form, logo, farve', 'Logo- eller farveafvigelse'],
        ['Accessories', 'Materiale, finish', 'Dårlig finish eller skade'],
        ['Tasker', 'Form, metaldele', 'Problemer med metaldele'],
      ],
    },
    faqs: [
      { q: 'Hvad er AllChinaBuy QC-billeder?', a: 'Billeder af din faktiske vare taget på lageret, så du kan bekræfte den inden afsendelse.' },
      { q: 'Hvordan beder jeg om QC-billeder?', a: 'Bed om dem i din konto, mens varen er på lageret, og angiv de ønskede vinkler.' },
      { q: 'Hvad skal jeg tjekke på QC-billeder?', a: 'Farve, størrelse, syninger, tryk og skader, sammenlignet med annoncen.' },
      { q: 'Hvad hvis varen ser forkert ud?', a: 'Kontakt agenten, mens varen er på lageret, hvor løsning er nemmest.' },
    ],
  },
  'allchinabuy-alternatives': {
    title: 'Bedste AllChinaBuy-alternativer: indkøbsagenter sammenlignet',
    shortDescription: 'En neutral sammenligning af de bedste AllChinaBuy-alternativer til at vælge den rette indkøbsagent.',
    metaTitle: 'Bedste AllChinaBuy-alternativer: indkøbsagenter sammenlignet',
    metaDescription: 'Leder du efter AllChinaBuy-alternativer? Sammenlign populære indkøbsagenter på produktopdagelse, fragt, lager og begyndervenlighed i denne neutrale guide.',
    h1: 'Bedste AllChinaBuy-alternativer: indkøbsagenter sammenlignet',
    intro: 'AllChinaBuy er én mulighed blandt flere indkøbsagenter, der hjælper internationale købere med at bestille fra kinesiske markedspladser og sende til udlandet. Denne guide sammenligner nogle kendte alternativer neutralt, så du kan afveje det vigtigste, før du vælger.',
    sections: [
      {
        heading: 'Hvorfor købere søger alternativer',
        paragraphs: [
          'Folk udforsker alternativer af forskellige grunde: nogle ønsker en anden gebyrstruktur, andre en bestemt fragtlinje eller en enklere grænseflade, og nogle kan bare godt lide at sammenligne før beslutningen.',
          'Da indkøbsagenter varierer i håndteringen af gebyrer, opbevaring, QC-billeder og support, er der ikke ét bedste valg for alle. Den rette agent afhænger af dit land, dine varer og hvor involveret du vil være.',
        ],
      },
      {
        heading: 'Hvad man skal sammenligne på tværs af agenter',
        paragraphs: [
          'Fokuser på det, der direkte påvirker din samlede pris og oplevelse. Servicegebyrer, fragtmuligheder, lagerbetingelser og QC-billeder er de mest almindelige forskelle.',
          'Begyndervenlighed betyder også noget. En klar grænseflade og hurtig support sparer dig for meget tid, især ved dine første ordrer.',
        ],
        list: [
          'Service- og håndteringsgebyrer',
          'Fragtlinjer og destinationer',
          'Lager og konsolidering',
          'QC-billeder og inspektion',
          'Kundesupport',
        ],
      },
      {
        heading: 'Et neutralt blik på populære muligheder',
        paragraphs: [
          'Flere agenter har opbygget et ry, hver med sine styrker. Frem for at rangordne dem er det mere nyttigt at tjekke de aktuelle vilkår for et par tjenester og se, hvilken der passer til dine sædvanlige ordrer.',
          'Kig efter gennemsigtige priser, klare fragtoplysninger og nyere tilbagemeldinger fra købere i dit land.',
        ],
      },
      {
        heading: 'Sådan træffer du det rette valg',
        paragraphs: [
          'Start med en lille ordre, når du prøver en ny agent, så du kan vurdere oplevelsen uden et stort engagement. Sammenlign den samlede pris inklusive gebyrer og fragt, ikke kun varens pris.',
        ],
        list: [
          'Sammenlign den samlede pris',
          'Tjek fragtmuligheder til din destination',
          'Læs nyere tilbagemeldinger fra dit land',
          'Start med en lille ordre',
        ],
      },
    ],
    comparison: {
      title: 'Hvad man skal tjekke ved sammenligning af agenter',
      columns: ['Faktor', 'Hvorfor det betyder noget', 'Hvad man skal bekræfte'],
      rows: [
        ['Servicegebyrer', 'Påvirker prisen', 'Tjek aktuelle gebyrer'],
        ['Fragt', 'Stor del af prisen', 'Sammenlign linjer til dit land'],
        ['Opbevaring', 'Muliggør konsolidering', 'Bekræft gratis periode'],
        ['QC-billeder', 'Hjælper med at verificere', 'Tjek tilgængelighed og pris'],
        ['Support', 'Sparer tid', 'Kig efter nyere feedback'],
      ],
    },
    faqs: [
      { q: 'Hvad er de bedste AllChinaBuy-alternativer?', a: 'Flere indkøbsagenter tilbyder lignende tjenester. Den bedste afhænger af din destination, dine varer og dine præferencer for gebyrer og fragt.' },
      { q: 'Hvordan sammenligner jeg indkøbsagenter?', a: 'Sammenlign servicegebyrer, fragtlinjer, lagerbetingelser, QC-billeder og nyere tilbagemeldinger, før du vælger.' },
      { q: 'Er der én bedste agent?', a: 'Nej, ingen agent er bedst for alle. Det rette valg afhænger af dit land, dine ordrer og din involvering.' },
      { q: 'Bør jeg teste med en lille ordre?', a: 'Ja, at starte småt er en fornuftig måde at vurdere en ny agent på, før du forpligter dig til større køb.' },
    ],
  },

  'allchinabuy-comparison': {
    title: 'AllChinaBuy-sammenligningsguide: sådan klarer den sig',
    shortDescription: 'En neutral AllChinaBuy-sammenligningsguide om funktioner, arbejdsgang og hvad man bør overveje.',
    metaTitle: 'AllChinaBuy-sammenligningsguide: sådan klarer den sig',
    metaDescription: 'En neutral AllChinaBuy-sammenligningsguide om produktopdagelse, arbejdsgang, lager, QC og fragt, så du kan træffe et informeret valg.',
    h1: 'AllChinaBuy-sammenligningsguide: sådan klarer den sig',
    intro: 'At sammenligne indkøbsagenter kan føles overvældende, fordi tjenesterne ligner hinanden ved første øjekast. Denne guide opdeler sammenligningen i de funktioner, der faktisk betyder noget, så du kan vurdere AllChinaBuy klart.',
    sections: [
      {
        heading: 'Den grundlæggende arbejdsgang',
        paragraphs: [
          'De fleste agenter, inklusive AllChinaBuy, følger samme flow: du tilføjer et produktlink, agenten køber varen, den ankommer til lageret, og så vælger du en fragtmetode.',
          'Agenterne adskiller sig i detaljerne omkring flowet — hvordan gebyrer er struktureret, hvordan opbevaring fungerer, og hvor meget kontrol du har over QC og konsolidering.',
        ],
      },
      {
        heading: 'Produktopdagelse og research',
        paragraphs: [
          'En god agent gør det let at finde og spore produkter. Strukturerede ressourcer som et regneark hjælper dig med at sammenligne varer, priser og sælgere ét sted.',
          'Her betaler organisering sig: jo tydeligere du kan se dine muligheder, jo bedre beslutninger træffer du.',
        ],
      },
      {
        heading: 'Lager, QC og fragt',
        paragraphs: [
          'Lageret er stedet, hvor dine varer opbevares, vejes og fotograferes inden afsendelse. QC-billeder lader dig verificere en vare, mens konsolidering hjælper med at kombinere flere ordrer.',
          'Fragt er ofte den største variable omkostning, så det er vigtigt at forstå de tilgængelige linjer og beregningen af vægt.',
        ],
      },
      {
        heading: 'Lav en afbalanceret sammenligning',
        paragraphs: [
          'Frem for at jagte en enkelt vinder, så list de funktioner, der betyder noget, og tjek hver agent mod dem. Gennemsigtige priser, klar support og nyere feedback er mere nyttige end markedsføringspåstande.',
        ],
      },
    ],
    comparison: {
      title: 'Funktionssammenligning oversigt',
      columns: ['Funktion', 'AllChinaBuy', 'Andre agenter'],
      rows: [
        ['Produktopdagelse', 'Understøttet', 'Generelt tilgængelig'],
        ['Regnearksressourcer', 'Almindelig', 'Varierer'],
        ['Lagerservice', 'Tilgængelig', 'Generelt tilgængelig'],
        ['QC-billeder', 'Tilgængelig', 'Varierer efter agent'],
        ['International fragt', 'Tilgængelig', 'Generelt tilgængelig'],
        ['Begynderhjælp', 'Tilgængelig', 'Varierer efter agent'],
      ],
    },
    faqs: [
      { q: 'Hvordan sammenligner AllChinaBuy sig med andre agenter?', a: 'Grundflowet er ens. Forskelle viser sig i gebyrer, fragtmuligheder, opbevaring og QC-billeder.' },
      { q: 'Hvilke funktioner skal jeg sammenligne først?', a: 'Start med gebyrer, fragtlinjer, lagerbetingelser og QC-billeder, da de påvirker din pris mest.' },
      { q: 'Er sammenligning nødvendig før køb?', a: 'Ikke påkrævet, men at sammenligne et par agenter undgår overraskelser og hjælper med at finde det rette valg.' },
      { q: 'Hvad er den vigtigste faktor?', a: 'For de fleste er den samlede pris (vare plus gebyrer og fragt) vigtigst, efterfulgt af pålidelighed og support.' },
    ],
  },

  'allchinabuy-discount-code': {
    title: 'AllChinaBuy-rabatkodeguide: spar mere',
    shortDescription: 'Sådan fungerer AllChinaBuy-rabatkoder, hvor du finder dem, og hvordan du undgår udløbne eller falske tilbud.',
    metaTitle: 'AllChinaBuy-rabatkodeguide: spar mere',
    metaDescription: 'Lær hvordan AllChinaBuy-rabatkoder fungerer, hvor du finder legitime tilbud, og hvordan du spotter udløbne eller falske koder.',
    h1: 'AllChinaBuy-rabatkodeguide: spar mere',
    intro: 'Rabatkoder kan tage lidt af din ordre, men kun hvis du finder legitime koder og anvender dem korrekt. Denne guide forklarer, hvordan rabatkoder fungerer, hvor tilbud kommer fra, og hvordan du skelner en ægte rabat fra spildt tid.',
    sections: [
      {
        heading: 'Sådan fungerer rabatkoder',
        paragraphs: [
          'En rabatkode er normalt en kort streng, du indtaster ved kassen for at reducere et gebyr eller få en procentdel af en del af ordren. Koder retter sig ofte mod fragt, servicegebyrer eller bestemte kategorier.',
          'De fleste koder har betingelser som et minimumskøb, en bestemt linje eller en udløbsdato, så læs altid vilkårene.',
        ],
      },
      {
        heading: 'Hvor man finder legitime koder',
        paragraphs: [
          'Det mest pålidelige sted er platformens egne meddelelser og kampagner. Sæsonudsalg og nyhedsbreve er andre almindelige kilder.',
          'Vær forsigtig med tredjepartssider, da deres koder ofte er forældede eller forkerte.',
        ],
      },
      {
        heading: 'Spot en falsk eller udløbet kode',
        paragraphs: [
          'Falske eller udløbne koder lover ofte urealistiske besparelser eller kommer fra sider, der beder om personlige oplysninger. Hvis et tilbud lyder for godt, er det det nok.',
          'En legitim kode kræver aldrig, at du deler login- eller betalingsoplysninger uden for det officielle kasseflow.',
        ],
        list: [
          'Tjek de officielle kanaler først',
          'Læs vilkår, udløb og minimum',
          'Undgå sider med enorme rabatter',
          'Del aldrig følsomme oplysninger for en kode',
        ],
      },
      {
        heading: 'Stable besparelser smart',
        paragraphs: [
          'Ud over koder sparer konsolidering og valg af den rette fragtlinje ofte mere end en lille rabat. At kombinere en gyldig kode med smarte fragtvalg giver det bedste resultat.',
        ],
      },
    ],
    comparison: {
      title: 'Legitim kode vs advarselstegn',
      columns: ['Tegn', 'Legitim', 'Pas på'],
      rows: [
        ['Kilde', 'Officielle kanaler', 'Ukendte tredjepartssider'],
        ['Værdi', 'Modest, realistisk', 'Enorm, ubegrænset'],
        ['Vilkår', 'Klart angivet', 'Uklare eller manglende'],
        ['Oplysninger', 'Ingen følsomme data', 'Beder om login eller betaling'],
      ],
    },
    faqs: [
      { q: 'Hvor finder jeg AllChinaBuy-rabatkoder?', a: 'Tjek platformens egne meddelelser og kampagner, da tilbud ændrer sig ofte og er mest pålidelige der.' },
      { q: 'Udløber rabatkoder?', a: 'Ja, de fleste har en udløbsdato og kan kræve et minimum eller gælde for bestemte tjenester.' },
      { q: 'Hvordan ved jeg, om en kode er falsk?', a: 'Vær på vagt over for enorme eller ubegrænsede rabatter og sider, der beder om personlige eller betalingsoplysninger.' },
      { q: 'Kan jeg kombinere en kode med andre besparelser?', a: 'Det afhænger af vilkårene. Selv uden stabling kan du spare ved at konsolidere og vælge den rette linje.' },
    ],
  },

  'allchinabuy-buying-guide': {
    title: 'Sådan køber du fra AllChinaBuy: komplet købsguide',
    shortDescription: 'En trin-for-trin-guide til at købe fra AllChinaBuy, fra at finde et produkt til at modtage din pakke.',
    metaTitle: 'Sådan køber du fra AllChinaBuy: komplet købsguide',
    metaDescription: 'Lær at købe fra AllChinaBuy trin for trin — find produkter, tilføj links, bestil, QC, konsolidering og fragt — i denne komplette begynderguide.',
    h1: 'Sådan køber du fra AllChinaBuy: komplet købsguide',
    intro: 'Hvis du aldrig har brugt en indkøbsagent, kan processen se forvirrende ud. Denne guide tager dig gennem hele rejsen i klare trin, fra at finde produktet til at åbne pakken ved døren.',
    sections: [
      {
        heading: 'Trin 1: find produktet',
        paragraphs: [
          'Start med at gennemse de markedspladser eller kategorier, du er interesseret i. Når du finder en vare, skal du kopiere det direkte link — det deler du med agenten.',
          'Vær opmærksom på annoncedetaljer, størrelser, farver og mål, så du vælger korrekt og reducerer risikoen for returnering.',
        ],
      },
      {
        heading: 'Trin 2: tilføj linket og bestil',
        paragraphs: [
          'Indsæt linket i din konto og indtast de ønskede detaljer, størrelse og farve. Agenten køber derefter varen for dig.',
          'Gennemgå ordren omhyggeligt, før du bekræfter, da ændringer efter køb er langsommere og mere komplicerede.',
        ],
      },
      {
        heading: 'Trin 3: vent på lageret og QC',
        paragraphs: [
          'Når sælgeren sender til agenten, ankommer varen til lageret og logges i din konto. Dette er det ideelle tidspunkt at bede om QC-billeder og verificere varen.',
          'Hvis noget ser forkert ud, så kontakt agenten, mens varen stadig er på lageret.',
        ],
      },
      {
        heading: 'Trin 4: konsolider og send',
        paragraphs: [
          'Når du er klar, skal du vælge en fragtmetode og bekræfte din adresse. At konsolidere flere varer i én pakke kan reducere din samlede fragtpris.',
          'Gem dit trackingnummer og følg pakken, indtil den ankommer, og svar hurtigt på told- eller leveringsmeddelelser.',
        ],
        list: [
          'Kopier det direkte produktlink',
          'Tilføj linket og bekræft størrelse og farve',
          'Bed om QC-billeder på lageret',
          'Konsolider varer og vælg en fragtlinje',
        ],
      },
    ],
    comparison: {
      title: 'Købstrin vs handling',
      columns: ['Trin', 'Handling', 'Hvorfor det betyder noget'],
      rows: [
        ['Find', 'Kopier linket', 'Sikrer den rette vare'],
        ['Bestil', 'Bekræft størrelse', 'Reducerer returnering'],
        ['QC', 'Bed om billeder', 'Verificerer før afsendelse'],
        ['Send', 'Konsolider og bekræft', 'Reducerer pris og fejl'],
      ],
    },
    faqs: [
      { q: 'Hvordan køber jeg fra AllChinaBuy?', a: 'Kopier et link, tilføj det med din størrelse og farve, lad agenten købe, og bed derefter om QC og vælg fragt.' },
      { q: 'Skal jeg kunne kinesisk?', a: 'Nej. Agenten håndterer kommunikationen med sælgere, hvilket er en af hovedfordelene ved en indkøbsagent.' },
      { q: 'Hvad hvis varen ankommer forkert?', a: 'QC-billeder lader dig fange problemer, mens varen stadig er på lageret.' },
      { q: 'Kan jeg kombinere flere ordrer?', a: 'Ja, at konsolidere flere varer i én pakke er en almindelig måde at reducere fragtprisen på.' },
    ],
  },

  'allchinabuy-sellers': {
    title: 'AllChinaBuy-sælgerguide: find pålidelige sælgere',
    shortDescription: 'Sådan researcher og sammenligner du AllChinaBuy-sælgere, så du kan bestille med større tryghed.',
    metaTitle: 'AllChinaBuy-sælgerguide: find pålidelige sælgere',
    metaDescription: 'Lær at researche AllChinaBuy-sælgere, sammenligne feedback og verificere annoncer, så du vælger pålidelige sælgere og undgår fejl.',
    h1: 'AllChinaBuy-sælgerguide: find pålidelige sælgere',
    intro: 'Kvaliteten af din indkøbsoplevelse afhænger ofte af de sælgere, du vælger. Denne guide forklarer, hvordan du researcher sælgere, hvad du skal kigge efter, og hvordan du sammenligner for at bestille med større tryghed.',
    sections: [
      {
        heading: 'Hvorfor sælgerresearch betyder noget',
        paragraphs: [
          'Sælgere varierer i kvalitet, kommunikation og pålidelighed. Et par minutter til research før bestilling undgår varer, der afviger fra annoncen, eller sælgere, der svarer langsomt.',
          'Selv med en agent drager du fordel af at vælge sælgere med klare annoncer og konsekvent feedback.',
        ],
      },
      {
        heading: 'Hvad man skal kigge efter i en annonce',
        paragraphs: [
          'En god annonce har klare billeder, detaljerede mål og præcise beskrivelser af materialer og farver. Vage eller kopierede annoncer er ofte et tegn på forsigtighed.',
          'Tjek om annoncen viser rigtige produktbilleder frem for kun lagerbilleder, og kig efter størrelsestabeller.',
        ],
        list: [
          'Klare, rigtige produktbilleder',
          'Detaljerede mål og størrelsestabeller',
          'Præcise materiale- og farvebeskrivelser',
          'Konsekvent nyere feedback',
        ],
      },
      {
        heading: 'Sammenlign sælgere',
        paragraphs: [
          'Når to sælgere tilbyder lignende varer, skal du sammenligne deres feedback, svartider og annoncedetaljer side om side. En lidt højere pris hos en mere lydhør sælger er ofte det værd.',
          'Hold din research organiseret i et regneark, så du kan sammenligne uden at miste overblikket.',
        ],
      },
      {
        heading: 'Brug QC-billeder til at verificere',
        paragraphs: [
          'QC-billeder er dit bedste værktøj til at verificere, at det en sælger sender matcher annoncen. Bed om præcise vinkler og sammenlign med den oprindelige annonce.',
        ],
      },
    ],
    comparison: {
      title: 'Sælgersignal vs hvad det fortæller',
      columns: ['Signal', 'Positiv', 'Forsigtighed'],
      rows: [
        ['Billeder', 'Rigtige og klare', 'Kun lagerbilleder'],
        ['Annonce', 'Detaljeret', 'Vag eller kopieret'],
        ['Feedback', 'Konsekvent', 'Sparsom eller modsigende'],
        ['Svar', 'Hurtig og klar', 'Langsom eller undvigende'],
      ],
    },
    faqs: [
      { q: 'Hvordan finder jeg pålidelige AllChinaBuy-sælgere?', a: 'Kig efter sælgere med klare rigtige billeder, detaljerede annoncer og konsekvent nyere feedback, og sammenlign før du bestiller.' },
      { q: 'Hvad skal jeg tjekke i en annonce?', a: 'Tjek rigtige billeder, detaljerede mål, materialebeskrivelser og en størrelsestabel, hvor relevant.' },
      { q: 'Hvordan hjælper QC-billeder med sælgere?', a: 'De lader dig verificere, at varen en sælger sender matcher annoncen, nyttigt til at spotte afvigelser.' },
      { q: 'Er en højere pris det værd for en bedre sælger?', a: 'Ofte ja. En mere lydhør, pålidelig sælger kan spare dig tid og problemer.' },
    ],
  },

  'allchinabuy-products': {
    title: 'AllChinaBuy-produktresearchguide: find de bedste varer',
    shortDescription: 'Sådan researcher du AllChinaBuy-produkter, sammenligner muligheder og finder de varer, der passer til dig.',
    metaTitle: 'AllChinaBuy-produktresearchguide',
    metaDescription: 'Lær at researche AllChinaBuy-produkter effektivt — sammenlign muligheder, tjek detaljer og organiser din research for at finde de bedste varer.',
    h1: 'AllChinaBuy-produktresearchguide: find de bedste varer',
    intro: 'Med så mange tilgængelige produkter er god research forskellen mellem et godt køb og en skuffelse. Denne guide forklarer, hvordan du researcher, sammenligner og organiserer det, du finder.',
    sections: [
      {
        heading: 'Start med en klar idé',
        paragraphs: [
          'Før du søger, skal du definere, hvad du faktisk har brug for — stil, størrelse, budget og must-have-funktioner. Et klart udgangspunkt hjælper med at filtrere det store antal resultater.',
          'Skriv dine nøglekriterier ned, og brug dem til at vurdere hvert produkt.',
        ],
      },
      {
        heading: 'Sammenlign lignende produkter',
        paragraphs: [
          'Når du finder flere kandidater, skal du sammenligne dem på de detaljer, der betyder noget: materialer, mål, pris og sælgerfeedback. Et regneark gør det meget lettere.',
          'Fokuser på forskelle, der påvirker din tilfredshed, som størrelse, farve og kvalitet, frem for kosmetiske variationer.',
        ],
      },
      {
        heading: 'Tjek de vigtige detaljer',
        paragraphs: [
          'Gennemgå altid mål, materialer og antallet af rigtige billeder. For tøj er en størrelsestabel vigtig. For sko skal du tjekke størrelsesnoter.',
        ],
        list: [
          'Mål og størrelsestabeller',
          'Materialer og konstruktion',
          'Rigtige produktbilleder',
          'Sælgerfeedback og lydhørhed',
        ],
      },
      {
        heading: 'Organiser din research',
        paragraphs: [
          'Hold din research i en struktureret liste med kolonner for produkt, link, pris, sælger og noter. Det holder alt ét sted.',
        ],
      },
    ],
    comparison: {
      title: 'Researchfaktor vs hvad man skal tjekke',
      columns: ['Faktor', 'Hvad man skal tjekke', 'Hvorfor det betyder noget'],
      rows: [
        ['Størrelse', 'Størrelsestabel', 'Undgår forkert pasform'],
        ['Materialer', 'Beskrivelse', 'Sætter forventninger'],
        ['Billeder', 'Rigtige billeder', 'Viser det rigtige produkt'],
        ['Sælger', 'Feedback', 'Indikerer pålidelighed'],
        ['Pris', 'Samlet pris', 'Holder budgettet'],
      ],
    },
    faqs: [
      { q: 'Hvordan researcher jeg AllChinaBuy-produkter?', a: 'Definer dine kriterier, sammenlign varer på detaljer og feedback, og organiser alt i en struktureret liste.' },
      { q: 'Hvad skal jeg tjekke før bestilling?', a: 'Tjek mål, materialer, rigtige billeder og feedback, og bekræft den samlede pris inklusive fragt.' },
      { q: 'Hvordan sammenligner jeg lignende produkter?', a: 'Placer dem side om side på de detaljer, der betyder noget: størrelse, materialer, pris og sælgerpålidelighed.' },
      { q: 'Hvorfor bruge et regneark?', a: 'Et regneark holder links, priser, sælgere og noter ét sted, hvilket letter sammenligning og beslutninger.' },
    ],
  },

  'allchinabuy-safety': {
    title: 'AllChinaBuy-sikkerhedsguide: handl med tryghed',
    shortDescription: 'Praktiske sikkerhedstips til at handle via AllChinaBuy, fra betalinger til personlige oplysninger.',
    metaTitle: 'AllChinaBuy-sikkerhedsguide: handl med tryghed',
    metaDescription: 'En praktisk AllChinaBuy-sikkerhedsguide om betalinger, QC-billeder, kontosikkerhed og personlige oplysninger, så du kan handle med tryghed.',
    h1: 'AllChinaBuy-sikkerhedsguide: handl med tryghed',
    intro: 'At handle via en agent er generelt sikkert, når du følger nogle få fornuftige praksisser. Denne guide dækker grundlæggende sikkerhed — betalinger, QC, kontosikkerhed og personlige oplysninger — så du kan fokusere på at finde gode varer.',
    sections: [
      {
        heading: 'Betal kun gennem officielle kanaler',
        paragraphs: [
          'Gennemfør altid betalinger via den officielle kasse. Undgå at sende penge direkte til enkeltpersoner, der hævder at behandle din ordre, da det fjerner din beskyttelse.',
          'Gem optegnelser over dine betalinger og ordrer, da de er nyttige ved tvister.',
        ],
      },
      {
        heading: 'Brug QC-billeder til at verificere',
        paragraphs: [
          'QC-billeder er et af de stærkeste sikkerhedsværktøjer, da de viser den faktiske vare før afsendelse. Bed om præcise vinkler og gennemgå dem omhyggeligt før bekræftelse.',
          'At fange et problem på lageret er langt lettere end at løse det efter international afsendelse.',
        ],
      },
      {
        heading: 'Beskyt din konto og dine oplysninger',
        paragraphs: [
          'Brug et stærkt, unikt kodeord, og del aldrig dine loginoplysninger. Vær på vagt over for beskeder eller sider, der beder om dine oplysninger uden for den officielle proces.',
          'Indtast aldrig betalingsoplysninger på tredjepartssider, der lover rabatter eller hurtigere levering.',
        ],
        list: [
          'Betal kun gennem officiel kasse',
          'Brug QC-billeder til at verificere',
          'Brug et stærkt, unikt kodeord',
          'Del aldrig login- eller betalingsoplysninger',
          'Undgå tredjepartssider, der beder om oplysninger',
        ],
      },
      {
        heading: 'Forstå fragt- og toldrisici',
        paragraphs: [
          'International fragt indebærer altid nogen risiko, herunder forsinkelser og toldgebyrer. At forstå dit lands toldregler og vælge forsikring hjælper med at planlægge.',
        ],
      },
    ],
    comparison: {
      title: 'Sikkerhedspraksis vs beskyttelse',
      columns: ['Praksis', 'Hvad den beskytter', 'Risiko undgået'],
      rows: [
        ['Officielle betalinger', 'Dine penge', 'Svig og tvister'],
        ['QC-billeder', 'Vareoverensstemmelse', 'Forkerte varer'],
        ['Stærkt kodeord', 'Din konto', 'Uautoriseret adgang'],
        ['Ingen tredjepartsoplysninger', 'Personlige data', 'Phishing'],
      ],
    },
    faqs: [
      { q: 'Er AllChinaBuy sikkert at bruge?', a: 'Ja, når du følger grundlæggende praksis som at betale via officielle kanaler, bruge QC-billeder og beskytte dine oplysninger.' },
      { q: 'Hvordan holder jeg mine betalinger sikre?', a: 'Betal altid via den officielle kasse og undgå at sende penge direkte til enkeltpersoner.' },
      { q: 'Hvad gør jeg, hvis en vare ser forkert ud?', a: 'Brug QC-billeder til at fange problemer, mens varen stadig er på lageret.' },
      { q: 'Skal jeg være forsigtig med tredjepartssider?', a: 'Ja. Indtast aldrig betalings- eller loginoplysninger på tredjepartssider.' },
    ],
  },

  'allchinabuy-tutorial': {
    title: 'AllChinaBuy-begyndertutorial: din første ordre gjort enkel',
    shortDescription: 'En venlig begyndertutorial til AllChinaBuy, der tager dig gennem din første ordre trin for trin.',
    metaTitle: 'AllChinaBuy-begyndertutorial: første ordre gjort enkel',
    metaDescription: 'En begyndervenlig AllChinaBuy-tutorial, der tager dig gennem din første ordre — fra produkt til modtagelse — i enkle trin.',
    h1: 'AllChinaBuy-begyndertutorial: din første ordre gjort enkel',
    intro: 'At starte med en indkøbsagent kan føles overvældende, men det bliver enkelt, når du forstår de grundlæggende trin. Denne tutorial tager dig gennem din første ordre i et enkelt sprog.',
    sections: [
      {
        heading: 'Hvad du har brug for, før du starter',
        paragraphs: [
          'Du har ikke brug for meget — bare et produkt, dets direkte link og en måde at betale på. Det hjælper at have din adresse og størrelsespræferencer klar.',
          'Realistiske forventninger er også nyttige: levering tager tid, og den samlede pris inkluderer gebyrer og fragt.',
        ],
      },
      {
        heading: 'Afgiv din allerførste ordre',
        paragraphs: [
          'Kopier linket og indsæt det i din konto, og vælg derefter størrelse og farve. Gennemgå alt før bekræftelse, da ændringer senere er langsommere.',
          'Efter agenten har købt varen, sendes den til lageret og logges i din konto.',
        ],
      },
      {
        heading: 'På lageret: QC og opbevaring',
        paragraphs: [
          'Når din vare ankommer til lageret, kan du bede om QC-billeder for at verificere. Du kan også vente og kombinere flere varer før afsendelse, hvilket ofte sparer penge.',
          'Hvis noget ser forkert ud, så kontakt agenten, mens varen stadig er på lageret.',
        ],
      },
      {
        heading: 'Send og modtag din pakke',
        paragraphs: [
          'Vælg en fragtmetode, bekræft din adresse og betal fragtgebyret. Du får et trackingnummer til at følge pakken til døren.',
        ],
        list: [
          'Kopier produktlinket',
          'Vælg størrelse og farve, og bekræft',
          'Bed om QC-billeder på lageret',
          'Vælg fragt og følg pakken',
        ],
      },
    ],
    comparison: {
      title: 'Fase vs hvad der sker',
      columns: ['Fase', 'Hvad der sker', 'Din rolle'],
      rows: [
        ['Ordre', 'Agenten køber', 'Tilføj link og bekræft'],
        ['Lager', 'Vare logget og vejet', 'Bed om QC-billeder'],
        ['Konsolidering', 'Varer kombineret', 'Vælg varer at sende sammen'],
        ['Fragt', 'Pakke afsendt', 'Vælg en linje og følg'],
      ],
    },
    faqs: [
      { q: 'Er AllChinaBuy svært for begyndere?', a: 'Ikke rigtig. Processen er en række enkle trin — tilføj et link, bekræft, bed om QC og vælg fragt — som du lærer hurtigt.' },
      { q: 'Hvad skal jeg forberede før min første ordre?', a: 'Hav dit link, din adresse og størrelsespræferencer klar, og husk, at prisen inkluderer gebyrer og fragt.' },
      { q: 'Hvor lang tid tager hele processen?', a: 'Det afhænger af sælgeren, lageret og metoden. International levering kan tage fra få dage til et par uger.' },
      { q: 'Kan jeg bede om hjælp under min første ordre?', a: 'Ja, de fleste agenter tilbyder support, du kan kontakte, hvis noget er uklart.' },
    ],
  },

  'allchinabuy-shipping-time': {
    title: 'AllChinaBuy-fragttid: hvad du kan forvente',
    shortDescription: 'Forstå AllChinaBuy-fragttid, hvad der påvirker den, og hvordan du estimerer dit leveringsvindue.',
    metaTitle: 'AllChinaBuy-fragttid: hvad du kan forvente',
    metaDescription: 'Lær hvad der påvirker AllChinaBuy-fragttid, hvordan metoder sammenlignes, og hvordan du estimerer dit leveringsvindue mere præcist.',
    h1: 'AllChinaBuy-fragttid: hvad du kan forvente',
    intro: 'Fragttid er et af de første spørgsmål nye købere stiller, og med god grund. Denne guide forklarer, hvad der påvirker fragttid, hvordan metoder sammenlignes, og hvordan du estimerer dit leveringsvindue.',
    sections: [
      {
        heading: 'Hvad påvirker fragttid',
        paragraphs: [
          'Flere faktorer bestemmer, hvor lang tid din pakke tager, startende med fragtmetoden. Dit destinationsland, toldbehandling og årstiden spiller også en rolle.',
          'Højsæson og helligdage kan tilføje forsinkelser, så tillad ekstra tid til en bestemt dato.',
        ],
      },
      {
        heading: 'Sådan sammenlignes metoder',
        paragraphs: [
          'Express-linjer er typisk hurtigst men dyrere, mens økonomilinjer er langsommere men billigere. Standardlinjer ligger imellem.',
          'Det bedste valg afhænger af, hvor presserende du har brug for varen, og dit budget.',
        ],
      },
      {
        heading: 'Estimer dit leveringsvindue',
        paragraphs: [
          'Et realistisk estimat kombinerer sælgerens behandlingstid, tiden til lageret og transittiden for metoden. At tjekke estimater for hvert trin giver et klarere billede.',
          'Brug dit trackingnummer, når pakken er afsendt, for det mest opdaterede billede.',
        ],
      },
      {
        heading: 'Undgå unødvendige forsinkelser',
        paragraphs: [
          'Tjek din adresse, svar hurtigt på told- eller leveringsmeddelelser, og konsolider varer tidligt. Disse små trin hjælper.',
        ],
        list: [
          'Vælg en metode, der passer til din hast',
          'Tillad ekstra tid omkring helligdage',
          'Tjek din adresse',
          'Følg pakken og svar på meddelelser',
        ],
      },
    ],
    comparison: {
      title: 'Metode vs typisk hastighed',
      columns: ['Metode', 'Hastighed', 'Pris'],
      rows: [
        ['Express', 'Hurtigst', 'Højest'],
        ['Standard', 'Balanceret', 'Moderat'],
        ['Økonomi', 'Langsomst', 'Lavest'],
      ],
    },
    faqs: [
      { q: 'Hvor lang tid tager AllChinaBuy-fragt?', a: 'Det afhænger af metoden og din destination. Express-linjer er hurtige, økonomilinjer langsommere.' },
      { q: 'Hvad påvirker fragttid?', a: 'Metoden, din destination, told og årstiden påvirker, hvor lang tid din pakke tager.' },
      { q: 'Hvordan estimerer jeg mit leveringsvindue?', a: 'Kombiner sælgerens tid, lagertid og transit, og brug dit trackingnummer til opdateringer.' },
      { q: 'Forårsager helligdage forsinkelser?', a: 'Ja, højsæson og helligdage tilføjer ofte forsinkelser, så tillad ekstra tid.' },
    ],
  },

  'allchinabuy-shipping-methods': {
    title: 'AllChinaBuy-fragtmetoder: find den rette linje',
    shortDescription: 'En guide til AllChinaBuy-fragtmetoder, deres forskelle og hvordan du vælger den rette linje.',
    metaTitle: 'AllChinaBuy-fragtmetoder: find den rette linje',
    metaDescription: 'Forstå AllChinaBuy-fragtmetoder — express, standard og økonomi — og lær at vælge den rette linje til din pakke.',
    h1: 'AllChinaBuy-fragtmetoder: find den rette linje',
    intro: 'At vælge den rette fragtmetode kan spare dig penge eller få din pakke hurtigere frem. Denne guide forklarer hovedtyperne af fragtlinjer, deres forskelle og hvordan du vælger den bedste.',
    sections: [
      {
        heading: 'Hovedtyperne af fragtlinjer',
        paragraphs: [
          'Fragtlinjer falder generelt i tre kategorier: express, standard og økonomi. Express er hurtigst men dyrest, økonomi billigst men langsomst, og standard balancerer de to.',
          'Inden for disse kategorier kan bestemte linjer passe bedre til visse destinationer eller størrelser, så sammenlign mulighederne for din pakke.',
        ],
      },
      {
        heading: 'Sådan vælger du den rette metode',
        paragraphs: [
          'Tænk over, hvor presserende du har brug for varen, og dit budget. Uden hastværk kan en økonomilinje spare meget. Hvis hastighed betyder noget, er express prisen værd.',
          'Overvej også varens værdi — for værdifulde varer er en metode med pålidelig sporing og forsikring sikrere.',
        ],
        list: [
          'Tilpas metoden til din hast',
          'Balancer hastighed og pris',
          'Tjek sporing og forsikring',
          'Overvej din destinations regler',
        ],
      },
      {
        heading: 'Vægt, volumen og pris',
        paragraphs: [
          'Husk, at kurerer fakturerer efter faktisk eller volumetrisk vægt, alt efter hvad der er højest. Kompakt pakning kan reducere den fakturerede vægt og din pris.',
        ],
      },
      {
        heading: 'Sporing og forsikring pr. metode',
        paragraphs: [
          'De fleste metoder inkluderer sporing, men detaljeniveau og forsikring kan variere. For værdifulde varer skal du bekræfte, at din linje understøtter forsikring.',
        ],
      },
    ],
    comparison: {
      title: 'Metode vs nøglekarakteristika',
      columns: ['Metode', 'Hastighed', 'Pris', 'Bedst til'],
      rows: [
        ['Express', 'Hurtig', 'Høj', 'Hastende eller værdifulde varer'],
        ['Standard', 'Balanceret', 'Moderat', 'De fleste ordrer'],
        ['Økonomi', 'Langsom', 'Lav', 'Ikke-hastende ordrer'],
      ],
    },
    faqs: [
      { q: 'Hvilke fragtmetoder tilbyder AllChinaBuy?', a: 'Fragtlinjer falder i express-, standard- og økonomikategorier, med linjer tilpasset visse destinationer.' },
      { q: 'Hvilken metode skal jeg vælge?', a: 'Tilpas metoden til din hast og dit budget: express for hastighed, økonomi for at spare, standard for balance.' },
      { q: 'Inkluderer alle metoder sporing?', a: 'De fleste inkluderer sporing, men detalje og forsikring varierer, så bekræft før afsendelse af værdifulde varer.' },
      { q: 'Påvirker pakning fragtprisen?', a: 'Ja, kurerer fakturerer faktisk eller volumetrisk vægt, så kompakt pakning reducerer vægt og pris.' },
    ],
  },
};

export default da;